import { useAtom } from 'jotai';
import { DateTime } from 'luxon';
import { useCallback, useEffect } from 'react';

import {
  feedbackToastStateAtom,
  feedbackToastsMetadataStateAtom,
} from 'common/state/feedbackToastState';

import { FeedbackForm } from '../api-types/director';
import { OperationType } from '../components/cookbook/Feedback/telemetry';

const isToastExpired = (toastExpirationTime: string) =>
  DateTime.now() > DateTime.fromISO(toastExpirationTime);

type FeedbackToastOptions = {
  onDismiss?: () => void;
  onSuccess?: (params: FeedbackForm) => void;
  id: string;
  title: string;
  /**
   * Operation for which feedback toast is displayed. Required for telemetry.
   */
  operationType: OperationType;
  expireDays: number;
  enabled?: boolean;
  warehouseId: string;
};

export function useFeedbackToast() {
  const [feedbackToast, setFeedbackToast] = useAtom(feedbackToastStateAtom);
  const [feedbackToastsMetadata, setFeedbackToastsMetadata] = useAtom(
    feedbackToastsMetadataStateAtom,
  );

  useEffect(() => {
    return () => {
      setFeedbackToast(null);

      setFeedbackToastsMetadata((prevMetadata) =>
        prevMetadata.map((item) => {
          return {
            ...item,
            // closed: true indicates the toast has been already displayed at least once to users.
            // Once it expires, reset to false so it can be displayed in the next trigger.
            closed: !isToastExpired(item.expirationTime),
          };
        }),
      );
    };
  }, [setFeedbackToast, setFeedbackToastsMetadata]);

  const showFeedbackToast = useCallback(
    ({
      id,
      title,
      operationType,
      expireDays,
      onDismiss,
      onSuccess,
      enabled = true,
      warehouseId,
    }: FeedbackToastOptions) => {
      if (!enabled) return;

      const storedToastMetadata = feedbackToastsMetadata.find((m) => m.id === id);

      if (storedToastMetadata) {
        const expired = isToastExpired(storedToastMetadata.expirationTime);

        if (!expired) {
          return;
        }
      }

      setFeedbackToast({
        id,
        title,
        operationType,
        onDismiss,
        onSuccess,
        warehouseId,
      });

      setFeedbackToastsMetadata((metadata) => [
        ...metadata.filter((m) => m.id !== id),
        {
          id,
          expirationTime: DateTime.now().plus({ days: expireDays }).toISO() || '',
          closed: false,
        },
      ]);
    },
    [feedbackToastsMetadata, setFeedbackToast, setFeedbackToastsMetadata],
  );

  const closeFeedbackToast = useCallback(
    (id: string) => {
      const closedFeedbackToast = feedbackToastsMetadata.find((m) => m.id === id);

      if (closedFeedbackToast) {
        setFeedbackToastsMetadata((metadata) => [
          ...metadata.filter((m) => m.id !== id),
          {
            ...closedFeedbackToast,
            closed: true,
          },
        ]);

        setFeedbackToast(null);
      }
    },
    [feedbackToastsMetadata, setFeedbackToast, setFeedbackToastsMetadata],
  );

  return {
    showFeedbackToast,
    feedbackToast,
    closeFeedbackToast,
  };
}
